/** Convidar alguém para o espaço (SPEC-IDN-011).
 *
 * O servidor manda o convite por e-mail. O link continua na tela mesmo assim, e
 * não é redundância: o token aparece uma vez só e não é recuperável, e-mail
 * corporativo engole mensagem de sistema, e há quem prefira mandar pelo
 * WhatsApp. Por isso ele fica visível, selecionável e com botão de copiar.
 *
 * **A tela diz o que de fato aconteceu.** Com `email_enviado` falso ela não
 * afirma que enviou — quem convidou ficaria esperando por uma mensagem que não
 * vem, e a pessoa convidada, por um acesso que ninguém mandou.
 */

import { useState, type SyntheticEvent } from "react";

import { ApiError } from "@/lib/http";
import { Botao } from "@/shared/ui/Botao";
import { Campo } from "@/shared/ui/Campo";

import { PAPEIS, convidar, linkDoConvite, type ConviteEmitido, type PapelConvidavel } from "./api";

export function ConvidarPessoa({ aoFechar }: { aoFechar: () => void }) {
  const [nome, setNome] = useState("");
  const [email, setEmail] = useState("");
  const [papel, setPapel] = useState<PapelConvidavel>("seller");
  const [emitido, setEmitido] = useState<ConviteEmitido | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  async function enviar(evento: SyntheticEvent) {
    evento.preventDefault();
    if (nome.trim() === "" || email.trim() === "" || enviando) return;

    setEnviando(true);
    setErro(null);
    try {
      setEmitido(await convidar({ full_name: nome.trim(), email: email.trim(), role_code: papel }));
    } catch (falha) {
      setErro(falha instanceof ApiError ? falha.message : "Não foi possível criar o convite.");
    } finally {
      setEnviando(false);
    }
  }

  if (emitido !== null) {
    return <LinkGerado convite={emitido} aoFechar={aoFechar} />;
  }

  return (
    <form
      onSubmit={(evento) => void enviar(evento)}
      aria-label="Convidar pessoa"
      className="flex flex-wrap items-end gap-4 rounded-lg border border-borda bg-superficie p-4 shadow-e1"
    >
      <div className="min-w-[200px] flex-1">
        <Campo
          rotulo="Nome"
          value={nome}
          onChange={(evento) => {
            setNome(evento.target.value);
          }}
          placeholder="Rafael Souza"
          required
          autoFocus
        />
      </div>

      <div className="min-w-[220px] flex-1">
        <Campo
          rotulo="E-mail"
          type="email"
          value={email}
          onChange={(evento) => {
            setEmail(evento.target.value);
          }}
          placeholder="rafael@acme.com.br"
          required
        />
      </div>

      <label className="flex w-[220px] flex-col gap-1">
        <span className="font-texto text-caption text-texto-suave">Papel</span>
        <select
          value={papel}
          onChange={(evento) => {
            setPapel(evento.target.value as PapelConvidavel);
          }}
          className="h-10 rounded-sm border border-borda bg-superficie px-2 font-texto text-body text-texto focus-visible:outline focus-visible:outline-2 focus-visible:outline-acao-viva"
        >
          {PAPEIS.map((item) => (
            <option key={item.codigo} value={item.codigo}>
              {item.nome} — {item.descricao}
            </option>
          ))}
        </select>
      </label>

      <div className="flex gap-2">
        <Botao type="submit" carregando={enviando}>
          Enviar convite
        </Botao>
        <Botao type="button" variante="fantasma" onClick={aoFechar}>
          Cancelar
        </Botao>
      </div>

      {erro !== null && (
        <p role="alert" className="w-full font-texto text-caption text-carmim">
          {erro}
        </p>
      )}
    </form>
  );
}

function LinkGerado({ convite, aoFechar }: { convite: ConviteEmitido; aoFechar: () => void }) {
  const [copiado, setCopiado] = useState(false);
  const link = linkDoConvite(convite.token);

  async function copiar() {
    try {
      await navigator.clipboard.writeText(link);
      setCopiado(true);
    } catch {
      // `clipboard` exige contexto seguro e permissão. Sem ele o link continua
      // na tela, selecionável — o caminho manual nunca deixa de existir.
      setCopiado(false);
    }
  }

  return (
    <section
      aria-label="Convite gerado"
      className="flex flex-col gap-3 rounded-lg border border-borda bg-superficie p-4 shadow-e1"
    >
      <div>
        <h3 className="font-display text-title text-texto">
          {convite.email_enviado
            ? `Convite enviado para ${convite.email}`
            : `Convite pronto para ${convite.email}`}
        </h3>
        <p className="mt-1 font-texto text-caption text-texto-suave">
          {convite.email_enviado
            ? "A pessoa recebeu um e-mail com o link de acesso. Ele vale por 7 dias e serve uma vez só — o link abaixo é o mesmo, caso a mensagem não chegue."
            : "Mande este link para a pessoa pelo canal que você já usa. Ele vale por 7 dias e serve uma vez só."}
        </p>
      </div>

      {!convite.email_enviado && (
        // Âmbar porque exige ação: sem esta linha, quem convidou sai da tela
        // supondo que o e-mail saiu, e a pessoa convidada espera por uma
        // mensagem que não existe.
        <p role="alert" className="font-texto text-caption text-ambar">
          Não conseguimos enviar o e-mail. O convite vale assim mesmo — copie o link e mande por
          outro canal.
        </p>
      )}

      <p
        // Selecionável e quebrando em qualquer ponto: o link é longo e o
        // caminho manual precisa funcionar sem a área de transferência.
        className="select-all break-all rounded-sm border border-borda bg-canvas p-3 font-dados text-caption text-texto"
      >
        {link}
      </p>

      <p className="font-texto text-caption text-ambar">
        Este link aparece uma única vez. Se fechar sem copiar, será preciso convidar de novo.
      </p>

      <div className="flex items-center gap-3">
        <Botao onClick={() => void copiar()}>{copiado ? "Copiado" : "Copiar link"}</Botao>
        <Botao variante="fantasma" onClick={aoFechar}>
          Concluir
        </Botao>
        <span aria-live="polite" className="sr-only">
          {copiado ? "Link copiado para a área de transferência." : ""}
        </span>
      </div>
    </section>
  );
}
